import * as React from 'react';
import { AppComponentProps, NumberKeyedDictionary } from '../typescriptHelpers';
import NewFeatureAffiliationModal, { FeatureAffiliationRequest } from './NewFeatureAffiliationModal';
import { PlusOutlined } from '@ant-design/icons';
import { DisallowedTokensForFA, IntegrationCapability, RequireAtLeastOneOfTokensForFA } from '../types';
import {
    DeleteFeatureAffiliationRequest,
    NewFeatureAffiliationRequest,
    tryUpdateAppSettings,
    UpdateFeatureAffiliationPatternRequest,
} from '../actions/appSettings';
import { RootState } from '../reducers/reducers';
import { AppSettingsUpdatable } from '../actions/updatable';
import { connect, ConnectedProps } from 'react-redux';
import {
    allSupportedIntegrationTypesForCapability,
    getError,
    getIsLoading,
    getUpdated,
    validatePattern,
} from '../helpers';
import { AppSettingsActionsComponent, AppSettingsEditableInputComponent } from './AppSettingsTableActionsComponent';
import { compact, forIn } from 'lodash';
import styles from './styles/settingsStyles';
import Table from 'antd/lib/table';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import { RunwayUserAction } from '../helpers/aclHelpers';
import PermissionedActionButton from './HOCs/PermissionedActionButton';
import { selectApp, selectIsRunwayOrg, selectOrgProviders } from '../selectors/selectors';
import { globalMessage } from '../utils/globalMessage';
import Typography from 'antd/lib/typography';

export interface FeatureAffiliationTableData {
    id: string;
    integrationName: string;
    affiliationName: string;
    pattern: string;
}

interface State {
    newFeatureAffiliationModalVisible: boolean;
    featureAffiliationPatternUpdated: NumberKeyedDictionary<string>;
    featureAffiliationIsEditing: NumberKeyedDictionary<boolean>;
    featureAffiliationPatternIsValid: NumberKeyedDictionary<boolean>;
}

class FeatureAffiliationsComponent extends React.Component<Props, State> {
    constructor(props) {
        super(props);

        this.state = {
            newFeatureAffiliationModalVisible: false,
            featureAffiliationPatternUpdated: {},
            featureAffiliationIsEditing: {},
            featureAffiliationPatternIsValid: {},
        };
    }

    static getDerivedStateFromProps(nextProps: Props, prevState: State) {
        const updateFeatureAffiliationIsEditing = {
            ...prevState.featureAffiliationIsEditing,
        };

        const updatedFeatureAffiliationPatternUpdated = {
            ...prevState.featureAffiliationPatternUpdated,
        };

        forIn(nextProps.featureAffiliationPatternUpdated, (didUpdate, id) => {
            if (didUpdate) {
                updateFeatureAffiliationIsEditing[id] = false;
                updatedFeatureAffiliationPatternUpdated[id] = null;
            }
        });

        return {
            newFeatureAffiliationModalVisible:
                nextProps.addNewFeatureAffiliationCompleted ? false : prevState.newFeatureAffiliationModalVisible,
            featureAffiliationIsEditing: updateFeatureAffiliationIsEditing,
            featureAffiliationPatternUpdated: updatedFeatureAffiliationPatternUpdated,
        };
    }

    componentDidUpdate(prevProps) {
        // Error adding new FA
        if (!prevProps.newFeatureAffiliationError && this.props.newFeatureAffiliationError) {
            globalMessage.error(this.props.newFeatureAffiliationError);
        }

        // Success new FA added
        if (!prevProps.addNewFeatureAffiliationCompleted && this.props.addNewFeatureAffiliationCompleted) {
            globalMessage.success('New feature affiliation added!');
        }

        // Error updating existing FA
        forIn(this.props.featureAffiliationUpdateError, (error, id) => {
            const prevError = prevProps.featureAffiliationUpdateError[id];

            if (!prevError && error) {
                globalMessage.error(error);
            }
        });
    }

    onAddNewFeatureAffiliationClicked = () => {
        this.setState({
            newFeatureAffiliationModalVisible: true,
        });
    };

    confirmFeatureAffiliationDelete = (id: number) => {
        const request: DeleteFeatureAffiliationRequest = {
            orgId: this.props.orgId,
            appId: this.props.appId,
            featureAffiliationId: id,
        };

        this.props.deleteFeatureAffiliation(request);
    };

    saveUpdateFeatureAffiliationPattern = (id: number) => {
        const updatedPattern = this.state.featureAffiliationPatternUpdated[id];

        if (updatedPattern) {
            this.props.updateFeatureAffiliationPattern(
                {
                    orgId: this.props.orgId,
                    appId: this.props.appId,
                    featureAffiliationId: id,
                    pattern: updatedPattern,
                },
                id,
            );
        }
    };

    renderFeatureAffiliationPatternField: (record: FeatureAffiliationTableData) => JSX.Element = (record) => {
        const featureAffiliationIsEditing = this.state.featureAffiliationIsEditing[record.id];

        return (
            <AppSettingsEditableInputComponent
                id={record.id}
                isEditing={featureAffiliationIsEditing}
                defaultValue={record.pattern}
                textProps={{
                    code: true,
                }}
                rules={[
                    {
                        validator: (rule, value) => {
                            const pattern = value as string;

                            return validatePattern(
                                pattern,
                                'Feature affiliation',
                                RequireAtLeastOneOfTokensForFA,
                                DisallowedTokensForFA,
                            );
                        },
                    },
                ]}
                onValueChanged={(id, value, isValid) => {
                    const featureAffiliationPatternUpdated = {
                        ...this.state.featureAffiliationPatternUpdated,
                        [id]: value,
                    };
                    const featureAffiliationPatternIsValid = {
                        ...this.state.featureAffiliationPatternIsValid,
                        [id]: isValid,
                    };
                    this.setState({
                        featureAffiliationPatternUpdated,
                        featureAffiliationPatternIsValid,
                    });
                }}
            />
        );
    };

    onCancelAddNewFeatureAffiliation = () => {
        this.setState({
            ...this.state,
            newFeatureAffiliationModalVisible: false,
        });
    };

    addNewFeatureAffiliation = (newAffiliationRequest: FeatureAffiliationRequest) => {
        this.props.addNewFeatureAffiliation({
            appId: this.props.appId,
            orgId: this.props.orgId,
            affiliationType: newAffiliationRequest.affiliationType,
            pattern: newAffiliationRequest.pattern,
            integrationId: newAffiliationRequest.integrationId,
        });
    };

    renderFeatureAffiliationPatternActions: (record: FeatureAffiliationTableData) => JSX.Element = (record) => {
        const featureAffiliationIsEditing = this.state.featureAffiliationIsEditing[record.id];
        const saveIsLoading = this.props.featureAffiliationSaveIsLoading[record.id];
        const patternIsValid = this.state.featureAffiliationPatternIsValid[record.id];

        return (
            <AppSettingsActionsComponent
                recordId={record.id}
                isEditing={featureAffiliationIsEditing}
                saveIsLoading={saveIsLoading}
                saveDisabled={!patternIsValid}
                deleteConfirmationText="Are you sure you want to delete this feature affiliation?"
                saveButtonOnClick={(id) => this.saveUpdateFeatureAffiliationPattern(id as number)}
                editCancelButtonClicked={(id) => {
                    const featureAffiliationIsEditing = {
                        ...this.state.featureAffiliationIsEditing,
                        [id]: false,
                    };

                    const featureAffiliationPatternUpdated = {
                        ...this.state.featureAffiliationPatternUpdated,
                        [id]: null,
                    };

                    const featureAffiliationPatternIsValid = {
                        ...this.state.featureAffiliationPatternIsValid,
                        [id]: false,
                    };

                    this.setState({
                        featureAffiliationIsEditing,
                        featureAffiliationPatternUpdated,
                        featureAffiliationPatternIsValid,
                    });
                }}
                editStartButtonClicked={(id) => {
                    const featureAffiliationIsEditing = {
                        ...this.state.featureAffiliationIsEditing,
                        [id]: true,
                    };
                    this.setState({ featureAffiliationIsEditing });
                }}
                deleteConfirm={(id) => this.confirmFeatureAffiliationDelete(id as number)}
                editPermissionedActionType={RunwayUserAction.UAAddRemoveEditFeatureAffiliations}
                deletePermissionsedActionType={RunwayUserAction.UAAddRemoveEditFeatureAffiliations}
            />
        );
    };

    render() {
        return (
            <div ref={this.props.refProp}>
                <div style={{ marginTop: 24 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Space direction="vertical" size="small">
                            <h4 style={styles.appSettingsSectionTitleStyle}>Feature affiliations</h4>
                            <Text type="secondary">
                                The fields (such as labels or fix versions) used to categorize items for a given release
                                in your project management tool. Read more about tokenized patterns in our{' '}
                                <Typography.Link
                                    href="https://docs.runway.team/getting-started/pattern-strings-tokens"
                                    target="_blank"
                                >
                                    docs.
                                </Typography.Link>
                            </Text>
                        </Space>
                        <PermissionedActionButton
                            actionType={RunwayUserAction.UAAddRemoveEditFeatureAffiliations}
                            style={{ float: 'right' }}
                            icon={<PlusOutlined />}
                            onClick={this.onAddNewFeatureAffiliationClicked}
                        >
                            Add
                        </PermissionedActionButton>
                    </div>
                    <Table
                        style={{ marginTop: 12, marginBottom: 12 }}
                        size="small"
                        locale={{
                            emptyText: 'No feature affiliations added yet',
                        }}
                        columns={[
                            {
                                title: 'Integration type',
                                key: 'integrationName',
                                dataIndex: 'integrationName',
                            },
                            {
                                title: 'Affiliation type',
                                key: 'affiliationName',
                                dataIndex: 'affiliationName',
                            },
                            {
                                title: 'Affiliation pattern',
                                render: this.renderFeatureAffiliationPatternField,
                            },
                            {
                                title: '',
                                render: this.renderFeatureAffiliationPatternActions,
                            },
                        ]}
                        dataSource={this.props.featureAffiliations.map((fa, index) => {
                            return {
                                ...fa,
                                key: index,
                            };
                        })}
                        pagination={false}
                        bordered
                    ></Table>
                </div>
                <NewFeatureAffiliationModal
                    orgId={this.props.orgId}
                    appId={this.props.appId}
                    featureManagementIntegrations={this.props.featureManagementIntegrations}
                    open={this.state.newFeatureAffiliationModalVisible}
                    onCancelAddNewFeatureAffiliation={this.onCancelAddNewFeatureAffiliation}
                    onOkNewFeatureAffiliation={this.addNewFeatureAffiliation}
                    isLoading={this.props.newFeatureAffiliationIsLoading}
                />
            </div>
        );
    }
}

interface FeatureAffiliationsComponentProps extends AppComponentProps {
    refProp?: React.MutableRefObject<HTMLDivElement>;
}

const mapProps = (state: RootState, ownProps: FeatureAffiliationsComponentProps) => {
    const { orgId, appId } = ownProps;

    const app = selectApp(state, appId);
    const integrationProviders = selectOrgProviders(state, orgId);

    const featureManagementIntegrations = app.appSettings.integrations.filter((integration) =>
        integration.capabilities.includes(IntegrationCapability.FeatureManagement),
    );
    const newFeatureAffiliationIsLoading = getIsLoading(AppSettingsUpdatable.NewFeatureAffiliation, state);
    const addNewFeatureAffiliationCompleted = getUpdated(AppSettingsUpdatable.NewFeatureAffiliation, state);
    const newFeatureAffiliationError = getError(AppSettingsUpdatable.NewFeatureAffiliation, state);

    const featureAffiliationSaveIsLoading: NumberKeyedDictionary<boolean> = {};
    const featureAffiliationPatternUpdated: NumberKeyedDictionary<boolean> = {};
    const featureAffiliationUpdateError: NumberKeyedDictionary<string> = {};
    const isRunwayOrg = selectIsRunwayOrg(state);

    const featureAffiliations: FeatureAffiliationTableData[] = compact(
        app.appSettings.featureAffiliations.map((fa) => {
            const integration = app.appSettings.integrations.find((integration) => integration.id == fa.integrationId);
            let affiliation = null;

            if (integration) {
                if (
                    allSupportedIntegrationTypesForCapability(
                        IntegrationCapability.FeatureManagement,
                        isRunwayOrg,
                        orgId,
                    ).includes(integration.id)
                ) {
                    const provider = integrationProviders.find((provider) => provider.id === integration.providerId);

                    if (provider) {
                        affiliation = provider.data.featureAffiliationTypes.find(
                            (affiliation) => affiliation.affiliationType == fa.affiliationType,
                        );
                    }
                }

                const faSaveIsLoading = getIsLoading(AppSettingsUpdatable.UpdateFeatureAffiliation, state, fa.id);
                const faUpdated = getUpdated(AppSettingsUpdatable.UpdateFeatureAffiliation, state, fa.id);
                const error = getError(AppSettingsUpdatable.UpdateFeatureAffiliation, state, fa.id);

                featureAffiliationSaveIsLoading[fa.id] = faSaveIsLoading;
                featureAffiliationPatternUpdated[fa.id] = faUpdated;
                featureAffiliationUpdateError[fa.id] = error;

                return {
                    id: fa.id,
                    integrationName: integration.name,
                    affiliationName: affiliation?.name,
                    pattern: fa.pattern,
                };
            } else {
                return null;
            }
        }),
    );

    return {
        featureManagementIntegrations,
        featureAffiliations,
        newFeatureAffiliationIsLoading,
        newFeatureAffiliationError,
        addNewFeatureAffiliationCompleted,
        featureAffiliationSaveIsLoading,
        featureAffiliationPatternUpdated,
        featureAffiliationUpdateError,
        orgId,
        appId,
        refProp: ownProps.refProp,
    };
};

const mapDispatch = (dispatch) => {
    return {
        addNewFeatureAffiliation: (request: NewFeatureAffiliationRequest) => {
            dispatch(tryUpdateAppSettings(AppSettingsUpdatable.NewFeatureAffiliation, request));
        },
        deleteFeatureAffiliation: (request: DeleteFeatureAffiliationRequest) => {
            dispatch(tryUpdateAppSettings(AppSettingsUpdatable.DeleteFeatureAffiliation, request));
        },
        updateFeatureAffiliationPattern: (request: UpdateFeatureAffiliationPatternRequest, id: number) => {
            dispatch(tryUpdateAppSettings(AppSettingsUpdatable.UpdateFeatureAffiliation, request, id));
        },
    };
};

const connector = connect(mapProps, mapDispatch);

type Props = ConnectedProps<typeof connector>;

export default connector(FeatureAffiliationsComponent);
